/** @jsxImportSource preact */
import type { CartSummaryData, CartLineItem } from '../types';
import type { ActionHandler } from './types';
import { sanitizeUrl } from '../services/sanitize';

interface CartSummaryProps {
  cart: CartSummaryData;
  onAction: ActionHandler;
  /** The cart panel has its own footer CTA: it mounts this with `false`. */
  showCheckout?: boolean;
}

function formatMoney(value: number | undefined, currency?: string): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  const amount = value.toLocaleString('es-AR');
  return currency ? `${currency} ${amount}` : `$${amount}`;
}

function CartLineItemRow({
  item,
  currency,
  onAction,
}: {
  item: CartLineItem;
  currency: string | undefined;
  onAction: ActionHandler;
}) {
  const imageUrl = sanitizeUrl(item.image_url || '');
  const itemId = item.item_id || item.product_id;
  const lineTotal =
    typeof item.unit_price === 'number' && typeof item.quantity === 'number'
      ? item.unit_price * item.quantity
      : undefined;

  return (
    <div class="lc-cart-item" data-item-id={itemId}>
      <div class="lc-cart-item-image">
        {imageUrl ? (
          <img src={imageUrl} alt={item.name || ''} loading="lazy" />
        ) : (
          <div class="lc-cart-item-no-image" data-empty="true" aria-hidden="true" />
        )}
      </div>
      <div class="lc-cart-item-info">
        <h4 class="lc-cart-item-name">{item.name}</h4>
        <div class="lc-cart-item-details">
          <span class="lc-cart-item-price">
            {formatMoney(item.unit_price, item.currency || currency)}
          </span>
          <span class="lc-cart-item-qty-controls">
            <button
              type="button"
              class="lc-cart-qty-btn"
              aria-label="Disminuir"
              onClick={() =>
                onAction({
                  kind: 'update-quantity',
                  itemId,
                  productName: item.name || '',
                  delta: -1,
                })
              }
            >
              -
            </button>
            <span class="lc-cart-item-qty">x{item.quantity}</span>
            <button
              type="button"
              class="lc-cart-qty-btn"
              aria-label="Aumentar"
              onClick={() =>
                onAction({
                  kind: 'update-quantity',
                  itemId,
                  productName: item.name || '',
                  delta: 1,
                })
              }
            >
              +
            </button>
          </span>
          {typeof lineTotal === 'number' ? (
            <span class="lc-cart-item-line-total">
              {formatMoney(lineTotal, item.currency || currency)}
            </span>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        class="lc-cart-item-remove"
        data-item-id={itemId}
        title="Eliminar"
        aria-label="Eliminar"
        onClick={() =>
          onAction({
            kind: 'remove-from-cart',
            itemId,
            productName: item.name || '',
          })
        }
      >
        x
      </button>
    </div>
  );
}

/**
 * Cart summary bubble. Renders the cart_summary envelope payload as
 * an inline component inside the message thread (separate from the
 * persistent cart panel which still lives in widget.ts for now).
 */
export function CartSummary({ cart, onAction, showCheckout = true }: CartSummaryProps) {
  if (!cart) return null;
  const items = Array.isArray(cart.items) ? cart.items : [];
  const totals = cart.totals || { subtotal: 0, total: 0 };
  const currency = totals.currency;

  return (
    <div class="lc-cart-summary">
      <div class="lc-cart-summary-items">
        {items.length === 0 ? (
          <div class="lc-cart-empty">
            <p>Tu carrito está vacío</p>
          </div>
        ) : (
          items.map((item) => (
            <CartLineItemRow
              key={`${item.item_id || item.product_id}-${item.variant_id || ''}`}
              item={item}
              currency={currency}
              onAction={onAction}
            />
          ))
        )}
      </div>
      <div class="lc-cart-summary-totals">
        <div class="lc-cart-panel-subtotal">
          <span>Subtotal</span>
          <span class="lc-cart-panel-subtotal-value">
            {formatMoney(totals.subtotal, currency)}
          </span>
        </div>
        {typeof totals.shipping === 'number' && totals.shipping > 0 ? (
          <div class="lc-cart-panel-shipping">
            <span>Envío</span>
            <span>{formatMoney(totals.shipping, currency)}</span>
          </div>
        ) : null}
        {typeof totals.tax === 'number' && totals.tax > 0 ? (
          <div class="lc-cart-panel-tax">
            <span>Impuestos</span>
            <span>{formatMoney(totals.tax, currency)}</span>
          </div>
        ) : null}
        <div class="lc-cart-panel-total">
          <span>Total</span>
          <span class="lc-cart-panel-total-value">
            {formatMoney(totals.total, currency)}
          </span>
        </div>
      </div>
      {showCheckout && items.length > 0 ? (
        <button
          type="button"
          class="lc-cart-panel-checkout"
          onClick={() => onAction({ kind: 'open-checkout' })}
        >
          Finalizar compra
        </button>
      ) : null}
    </div>
  );
}
